import {
  deleteForm,
  updateFormOrganisation,
  updateFormStatus,
} from "@/api/forms";
import { ApiError } from "@/api/client";
import { isFormListItemClosed } from "@/lib/forms/formMeta";
import { isGlobalManager, isYandexForm } from "@/lib/forms/formSource";
import type { FormListItemDto } from "@/types/formList";

export type BulkFormAction = "publish" | "close" | "delete";

export type BulkFormActionResult = {
  succeeded: number;
  failed: Array<{ id: number; name: string; message: string }>;
};

export function canManageListForm(
  form: FormListItemDto,
  canManageForm: (organisationId: number | null | undefined) => boolean,
  roles: string[],
): boolean {
  if (isYandexForm(form) && form.organisationId == null) {
    return isGlobalManager(roles);
  }
  return canManageForm(form.organisationId);
}

async function runSingleBulkAction(
  form: FormListItemDto,
  action: BulkFormAction,
): Promise<void> {
  if (action === "publish") {
    await updateFormStatus(form.id, { isClosed: false });
    return;
  }
  if (action === "close") {
    await updateFormStatus(form.id, { isClosed: true });
    return;
  }
  await deleteForm(form.id);
}

function actionErrorMessage(err: unknown, action: BulkFormAction): string {
  if (err instanceof ApiError) {
    if (err.message) return err.message;
    return `Ошибка ${err.status}`;
  }
  if (action === "delete") return "Не удалось удалить";
  return "Не удалось изменить статус";
}

export async function runBulkFormAction(
  forms: FormListItemDto[],
  action: BulkFormAction,
): Promise<BulkFormActionResult> {
  const result: BulkFormActionResult = { succeeded: 0, failed: [] };

  for (const form of forms) {
    try {
      if (action === "publish" && !isFormListItemClosed(form)) {
        result.succeeded += 1;
        continue;
      }
      if (action === "close" && isFormListItemClosed(form)) {
        result.succeeded += 1;
        continue;
      }

      await runSingleBulkAction(form, action);
      result.succeeded += 1;
    } catch (err) {
      result.failed.push({
        id: form.id,
        name: form.name,
        message: actionErrorMessage(err, action),
      });
    }
  }

  return result;
}

export function bulkActionLabel(action: BulkFormAction, count: number): string {
  const n = count;
  switch (action) {
    case "publish":
      return n === 1 ? "Опубликовать" : `Опубликовать (${n})`;
    case "close":
      return n === 1 ? "Закрыть" : `Закрыть (${n})`;
    case "delete":
      return n === 1 ? "Удалить" : `Удалить (${n})`;
  }
}

export function bulkDeleteConfirmMessage(count: number): string {
  const word =
    count % 10 === 1 && count % 100 !== 11
      ? "форму"
      : count % 10 >= 2 &&
          count % 10 <= 4 &&
          (count % 100 < 10 || count % 100 >= 20)
        ? "формы"
        : "форм";
  return `Удалить ${count} ${word}? Ответы и поля будут удалены безвозвратно.`;
}

export async function runBulkOrganisationChange(
  forms: FormListItemDto[],
  organisationId: number | null,
): Promise<BulkFormActionResult> {
  const result: BulkFormActionResult = { succeeded: 0, failed: [] };

  for (const form of forms) {
    try {
      if ((form.organisationId ?? null) === organisationId) {
        result.succeeded += 1;
        continue;
      }

      await updateFormOrganisation(form.id, organisationId);
      result.succeeded += 1;
    } catch (err) {
      result.failed.push({
        id: form.id,
        name: form.name,
        message:
          err instanceof ApiError
            ? err.message || `Ошибка ${err.status}`
            : "Не удалось сменить организацию",
      });
    }
  }

  return result;
}

export function bulkOrganisationConfirmMessage(
  count: number,
  organisationLabel: string,
): string {
  const word =
    count % 10 === 1 && count % 100 !== 11
      ? "форму"
      : count % 10 >= 2 &&
          count % 10 <= 4 &&
          (count % 100 < 10 || count % 100 >= 20)
        ? "формы"
        : "форм";
  return `Перенести ${count} ${word} в «${organisationLabel}»?`;
}

export function bulkOrganisationClearConfirmMessage(count: number): string {
  const word =
    count % 10 === 1 && count % 100 !== 11
      ? "форму"
      : count % 10 >= 2 &&
          count % 10 <= 4 &&
          (count % 100 < 10 || count % 100 >= 20)
        ? "формы"
        : "форм";
  return `Снять привязку к организации для ${count} ${word}?`;
}
